import { useState, useRef, useEffect } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Markdown } from "@/lib/markdown";
import { LockedFeature } from "@/components/LockedFeature";
import { toast } from "sonner";
import {
  FileSearch, Upload, Loader2, FileText, AlertTriangle, ShieldAlert, ListChecks,
  Handshake, HelpCircle, Sparkles, X, Image as ImageIcon, Search, History,
} from "lucide-react";

const DOC_TYPES = ["Auto-detect", "Contract", "Term Sheet", "NDA", "Partnership Agreement", "Lease Agreement", "Offer Letter", "Investment Agreement", "Service Agreement", "Other"];
const RISK = { Low: "#10B981", Medium: "#F59E0B", High: "#EF4444" };

export default function DealAnalyzer() {
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.tier === "pro";
  const [files, setFiles] = useState([]);
  const [text, setText] = useState("");
  const [docType, setDocType] = useState("Auto-detect");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => { if (isPro) api.get("/deal-analyzer/history").then((r) => setHistory(r.data)).catch(() => {}); }, [isPro]);

  if (!isPro) return (
    <LockedFeature title="Deal Analyzer" description="Upload any contract, term sheet, NDA or business document — PDF, Word, text or photos — and ECO breaks it down: summary, key clauses, risks, red flags, missing items and negotiation leverage."
      perks={["PDF, Word, text & multiple images", "Executive summary + plain-English breakdown", "Risk & red-flag detection", "Negotiation leverage & questions to ask"]} />
  );

  const pickFiles = (list) => {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    const valid = arr.filter((f) => {
      if (f.size > 12 * 1024 * 1024) { toast.error(`"${f.name}" is over 12MB and was skipped.`); return false; }
      return true;
    });
    if (valid.length) setFiles((prev) => [...prev, ...valid]);
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const analyze = async () => {
    if (!files.length && !text.trim()) { toast.error("Upload file(s) or paste text first"); return; }
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (text.trim()) fd.append("text", text.trim());
      fd.append("doc_type", docType === "Auto-detect" ? "" : docType);
      const start = await api.post("/deal-analyzer", fd);
      const jobId = start.data.job_id;
      // Analysis runs as a background job; poll until it's done (can take a few minutes).
      const deadline = Date.now() + 5 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await api.get(`/deal-analyzer/status/${jobId}`);
        if (s.data.status === "done") {
          setResult(s.data.result);
          api.get("/deal-analyzer/history").then((r) => setHistory(r.data)).catch(() => {});
          return;
        }
        if (s.data.status === "error") {
          toast.error(s.data.error || "Analysis failed. Please try again.");
          return;
        }
      }
      toast.error("Analysis is taking longer than expected. Please try again.");
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || e.message || "Analysis failed");
    } finally { setLoading(false); }
  };

  const isImg = (f) => f.type.startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(f.name);

  return (
    <div className="space-y-8" data-testid="deal-analyzer-page">
      <div>
        <span className="font-mono-eco text-xs uppercase tracking-widest text-slate-500">Deal Analyzer</span>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Understand any deal before you sign.</h1>
        <p className="text-slate-400 mt-2">Upload a contract, term sheet or agreement — or multiple photos of one. ECO reads it all together and tells you what matters.</p>
      </div>

      <div className="eco-card p-6 space-y-4">
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); pickFiles(e.dataTransfer.files); }}
          data-testid="deal-dropzone"
          className="border-2 border-dashed border-white/12 rounded-xl p-8 text-center cursor-pointer hover:border-[#0055FF]/50 hover:bg-white/[0.02] transition-all"
        >
          <input ref={inputRef} type="file" multiple accept=".pdf,.docx,.txt,.png,.jpg,.jpeg,.webp,.gif,image/*" className="hidden"
            data-testid="deal-file-input" onChange={(e) => { pickFiles(e.target.files); if (inputRef.current) inputRef.current.value = ""; }} />
          <div className="h-12 w-12 rounded-2xl bg-[#0055FF]/10 border border-[#0055FF]/20 flex items-center justify-center mx-auto mb-3">
            <Upload size={22} className="text-[#4d8bff]" />
          </div>
          <div className="text-white font-medium">Drop files or click to upload</div>
          <div className="text-xs text-slate-500 mt-1">PDF · Word (.docx) · Text · Images — add multiple pages/photos of one document (12MB each)</div>
        </div>

        {files.length > 0 && (
          <div className="space-y-2" data-testid="deal-selected-files">
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-3" data-testid={`deal-file-${i}`}>
                {isImg(f) ? <ImageIcon size={18} className="text-[#4d8bff] shrink-0" /> : <FileText size={18} className="text-[#4d8bff] shrink-0" />}
                <span className="text-sm text-slate-200 truncate flex-1">{f.name}</span>
                <button onClick={() => removeFile(i)} data-testid={`deal-remove-file-${i}`} className="text-slate-500 hover:text-white"><X size={16} /></button>
              </div>
            ))}
            <div className="text-xs text-slate-500">{files.length} file{files.length > 1 ? "s" : ""} — analyzed together as one document</div>
          </div>
        )}

        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="h-px flex-1 bg-white/10" /> or paste text <span className="h-px flex-1 bg-white/10" />
        </div>
        <textarea data-testid="deal-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={4}
          placeholder="Paste contract or agreement text here…" className="eco-input resize-none" />

        <div className="grid sm:grid-cols-2 gap-3">
          <select data-testid="deal-doctype" value={docType} onChange={(e) => setDocType(e.target.value)} className="eco-input">
            {DOC_TYPES.map((t) => <option key={t} value={t} className="bg-[#0A0A0A]">{t}</option>)}
          </select>
          <button data-testid="deal-analyze-btn" onClick={analyze} disabled={loading || (!files.length && !text.trim())} className="btn-accent">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <><Search size={16} /> Analyze document</>}
          </button>
        </div>
      </div>

      {result && (
        <div className="space-y-5" data-testid="deal-result">
          <div className="eco-card p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="font-mono-eco text-[10px] uppercase tracking-widest text-slate-500">Document type</div>
                <h2 className="font-display text-xl font-semibold text-white mt-1">{result.document_type || "Document"}</h2>
                <div className="text-xs text-slate-500 mt-1">{result.source}</div>
              </div>
              {result.overall_risk && (
                <span className="eco-tag flex items-center gap-1.5 shrink-0" style={{ color: RISK[result.overall_risk] || "#94a3b8", borderColor: `${RISK[result.overall_risk] || "#94a3b8"}33`, background: `${RISK[result.overall_risk] || "#94a3b8"}1a` }}>
                  <ShieldAlert size={13} /> {result.overall_risk} risk
                </span>
              )}
            </div>
            <h3 className="flex items-center gap-2 font-display text-base font-semibold text-white mb-2"><Sparkles size={17} className="text-[#4d8bff]" /> Executive summary</h3>
            <Markdown>{result.executive_summary}</Markdown>
            {result.recommendation && (
              <div className="mt-4 bg-[#0055FF]/10 border border-[#0055FF]/20 rounded-xl p-4 text-sm text-slate-200"><span className="font-semibold text-white">Recommendation: </span>{result.recommendation}</div>
            )}
          </div>

          {result.key_clauses?.length > 0 && (
            <div className="eco-card p-6">
              <h3 className="flex items-center gap-2 font-display text-base font-semibold text-white mb-4"><FileText size={17} className="text-[#4d8bff]" /> Key clauses</h3>
              <div className="space-y-3">
                {result.key_clauses.map((c, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid={`deal-clause-${i}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-medium text-white">{c.title}</div>
                      {c.risk_level && <span className="text-[10px] font-mono-eco uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0" style={{ color: RISK[c.risk_level] || "#94a3b8", background: `${RISK[c.risk_level] || "#94a3b8"}1a` }}>{c.risk_level}</span>}
                    </div>
                    <div className="text-sm text-slate-400 mt-1.5">{c.summary}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <ListBlock icon={AlertTriangle} title="Red flags" items={result.red_flags} color="#EF4444" testid="deal-redflags" />
            <ListBlock icon={ListChecks} title="Missing or worth adding" items={result.missing_items} color="#F59E0B" />
            <ListBlock icon={Handshake} title="Negotiation leverage" items={result.negotiation_points} color="#10B981" />
            <ListBlock icon={HelpCircle} title="Questions to ask" items={result.questions_to_ask} color="#A855F7" />
          </div>

          {result.key_points?.length > 0 && <ListBlock icon={ListChecks} title="Key points at a glance" items={result.key_points} color="#0055FF" />}

          {result.plain_english && (
            <div className="eco-card p-6">
              <h3 className="flex items-center gap-2 font-display text-base font-semibold text-white mb-2"><FileSearch size={17} className="text-[#4d8bff]" /> In plain English</h3>
              <Markdown>{result.plain_english}</Markdown>
            </div>
          )}
        </div>
      )}

      {!result && history.length > 0 && (
        <div className="eco-card p-6">
          <h3 className="flex items-center gap-2 font-display text-base font-semibold text-white mb-4"><History size={17} className="text-slate-400" /> Recent analyses</h3>
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-3" data-testid={`deal-history-${i}`}>
                <FileText size={16} className="text-slate-500 shrink-0" />
                <span className="text-sm text-slate-200 truncate flex-1">{h.source}</span>
                {h.overall_risk && <span className="text-[10px] font-mono-eco uppercase tracking-widest" style={{ color: RISK[h.overall_risk] || "#94a3b8" }}>{h.overall_risk}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ListBlock({ icon: I, title, items, color, testid }) {
  if (!items?.length) return null;
  return (
    <div className="eco-card p-6" data-testid={testid}>
      <h3 className="flex items-center gap-2 font-display text-base font-semibold text-white mb-4"><I size={17} style={{ color }} /> {title}</h3>
      <ul className="space-y-2.5">{items.map((it, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300"><span className="h-1.5 w-1.5 rounded-full mt-2 shrink-0" style={{ background: color }} /> {typeof it === "string" ? it : JSON.stringify(it)}</li>)}</ul>
    </div>
  );
}
